import React, { useState } from 'react'
import { TabsContextType } from '@/types/dashboard'

const TabsContext = React.createContext<TabsContextType | null>(null);

interface TabsProps {
  defaultValue: string
  children: React.ReactNode
}

interface TabsListProps {
  children: React.ReactNode
}

interface TabsTriggerProps {
  value: string
  children: React.ReactNode
}

interface TabsContentProps {
  value: string
  children: React.ReactNode
}

export function Tabs({ defaultValue, children }: TabsProps) {
  const [activeTab, setActiveTab] = useState(defaultValue);
  
  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabsList({ children }: TabsListProps) {
  return (
    <div className="flex bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 mb-6 shadow-monumenta border border-gray-800/50">
      {children}
    </div>
  );
}

export function TabsTrigger({ value, children }: TabsTriggerProps) {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('TabsTrigger must be used within Tabs');
  
  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;
  
  return (
    <button
      onClick={() => setActiveTab(value)}
      className={`px-4 py-3 rounded-monumenta font-display font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap flex-1 justify-center ${
        isActive 
          ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand transform scale-105' 
          : 'text-gray-300 hover:text-white hover:bg-brand-dark-lighter hover:scale-102'
      }`}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, children }: TabsContentProps) {
  const context = React.useContext(TabsContext);
  if (!context) throw new Error('TabsContent must be used within Tabs');
  
  const { activeTab } = context;
  if (activeTab !== value) return null;
  return <div className="animate-fade-in-up">{children}</div>;
}